import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import classNames from "classnames";
import clsx from "clsx";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useDebounceSearch } from "../../customeHooks/useDebounce";
import { PRIMARY_COLOR } from "../../utils/constants";
import { Icon } from "../icon/icon";
import { TextInput, TextInputProps } from "../inputs/textInput";
import { LoadingOval } from "../loaders/loaders";

// const handleSearch = (searchFunction: (val: any) => void, query: any) => {
//   searchFunction(query);
// };

// const handleDebouncedSearch = debounce(handleSearch, 800);

const ComboBox = ({
  menuItems,
  className = "",
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  onSearch = () => {},
  onMenuClose = () => {},
  onSelectOption = () => {},
  loading = false,
  immediate = false,
  showEmptyState = true,
  selected,
  customMenuItems = undefined,
  ...props
}: {
  menuItems?: {
    name: ReactNode;
    value?: string | object;
  }[];
  className?: string;
  menuClassName?: string;
  menuItemsClassName?: string;
  onSearch?: (val: string) => void;
  onMenuClose?: () => void;
  onSelectOption?: (val?: any) => void;
  loading?: boolean;
  immediate?: boolean;
  showEmptyState?: boolean;
  position?: AnchorProps;
  selected?: any;
  customMenuItems?: ReactNode | ((props: { open: boolean }) => ReactNode);
} & TextInputProps) => {
  const [width, setWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useDebounceSearch({
    searchQuery: props.value,
    onSearch: (query) => {
      onSearch(query as string);
    },
  });

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef?.current?.offsetWidth]);

  return (
    <Combobox
      as="div"
      className={`relative inline-block text-left !outline-none focus-within:!outline-none focus:!outline-none ${className}`}
      onChange={(val) => {
        if (val) {
          onSelectOption(val);
        }
      }}
      onClose={() => {
        onMenuClose();
      }}
      immediate={immediate}
    >
      {({ open }) => {
        return (
          <>
            <div
              className="w-full outline-none focus-within:outline-none focus:outline-none"
              ref={containerRef}
            >
              <ComboboxInput as={TextInput} autoFocus={false} {...props} />
            </div>

            {!showEmptyState && menuItems?.length === 0 ? undefined : (
              <Options
                menuItems={menuItems}
                menuClassName={menuClassName}
                menuItemsClassName={menuItemsClassName}
                position={position}
                loading={loading}
                showEmptyState={showEmptyState}
                selected={selected}
                width={width}
                open={open}
                customMenuItems={customMenuItems}
              />
            )}
          </>
        );
      }}
    </Combobox>
  );
};

const Options = ({
  menuItems,
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  loading = false,
  showEmptyState = true,
  selected,
  width,
  open,
  customMenuItems = undefined,
}: {
  menuItems?: {
    name: ReactNode;
    value?: string | object;
  }[];
  menuClassName?: string;
  menuItemsClassName?: string;
  loading?: boolean;
  showEmptyState?: boolean;
  position?: AnchorProps;
  selected?: any;
  width: number;
  open: boolean;
  customMenuItems?: ReactNode | ((props: { open: boolean }) => ReactNode);
}) => {
  return (
    <ComboboxOptions
      className={classNames(
        `min-w-ma relative !z-[999] flex w-full flex-col overflow-x-hidden overflow-y-scroll`,
        { "rounded-md border bg-white": customMenuItems === undefined },
        "[--anchor-gap:16px]",
        "outline-none focus-within:outline-none focus:outline-none",
        menuClassName,
      )}
      transition
      anchor={position}
      style={{
        width: width,
        maxWidth: `${width}px`,
      }}
      portal
    >
      {!loading ? (
        <>
          {customMenuItems !== undefined ? (
            <div onClick={undefined}>
              {typeof customMenuItems === "function"
                ? customMenuItems({ open })
                : customMenuItems}
            </div>
          ) : (
            menuItems?.map((item, index) => {
              const key = `menu-item-${index}`;
              const isSelected =
                selected &&
                JSON.stringify(item.value) === JSON.stringify(selected);

              return (
                <ComboboxOption
                  key={key}
                  as="button"
                  type="button"
                  value={item.value}
                  className={clsx(
                    "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
                    menuItemsClassName,
                  )}
                  data-selected={isSelected}
                  data-focus={isSelected}
                >
                  {item.name}
                </ComboboxOption>
              );
            })
          )}
        </>
      ) : undefined}

      {loading ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
          <LoadingOval
            loaderWidth="20"
            loaderHeight="20"
            color={PRIMARY_COLOR}
          />
        </div>
      ) : undefined}

      {menuItems && menuItems.length === 0 && !loading && showEmptyState ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
          <Icon
            svg="search-x"
            width={44}
            height={44}
            className="rounded-full bg-grey-100 p-3 text-grey-600"
          />

          <p className="text-sm">No Result Found</p>
        </div>
      ) : undefined}
    </ComboboxOptions>
  );
};

export default ComboBox;
